import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../images/favicon.png";
import { toast } from "react-toastify";
import { recoveryWithCode } from "../../services/authServices";

const RecoveryWithCode = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!recoveryCode) {
      newErrors.recoveryCode = "This field is required.";
    }
    if (!password) {
      newErrors.password = "This field is required.";
    } else if (password.length < 5) {
      newErrors.password = "Min 5 characters.";
    } else if (password.length > 100) {
      newErrors.password = "Max 100 characters.";
    }
    if (!passwordAgain) {
      newErrors.passwordAgain = "This field is required.";
    } else if (passwordAgain !== password) {
      newErrors.passwordAgain = "Password mismatch.";
    } else if (passwordAgain.length < 5) {
      newErrors.passwordAgain = "Min 5 characters.";
    } else if (passwordAgain.length > 100) {
      newErrors.passwordAgain = "Max 100 characters.";
    }
    return newErrors;
  };

  const recover = async (e) => {
    if (e) e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    const userdata = { recoveryCode, password };

    try {
      const response = await recoveryWithCode(userdata);
      setLoading(false);

      if (response.data.success) {
        toast.success("Password updated! You can sign in now!");
        navigate("/login");
      } else {
        toast.error(response.data.message || "Update failed! Try again later!");
      }
    } catch (error) {
      setLoading(false);
      toast.error("Update failed! Please try again.");
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "password") {
      setPassword(value);
    } else if (name === "passwordAgain") {
      setPasswordAgain(value);
    } else if (name === "recoveryCode") {
      setRecoveryCode(value);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-between bg-gray-50">
      <div className="flex flex-1 items-center justify-center">
        <div className="sm:m-3 w-full max-w-md bg-white rounded-2xl shadow-md p-8 flex flex-col items-center">
          <div className="flex flex-col items-center mb-6">
            <img src={logo} alt="Logo" className="h-12 mb-4" />
            <h1 className="text-2xl font-semibold text-gray-800">
              Password Recovery
            </h1>
          </div>
          <form onSubmit={recover} className="w-full">
            <div className="mb-4">
              <label
                htmlFor="recoveryCode"
                className="block text-gray-700 mb-1"
              >
                Recovery Code*
              </label>
              <input
                id="recoveryCode"
                name="recoveryCode"
                type="text"
                value={recoveryCode}
                onChange={handleChange}
                className={`w-full border ${
                  errors.recoveryCode ? "border-red-500" : "border-gray-300"
                } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700`}
                autoComplete="off"
              />
              {errors.recoveryCode && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.recoveryCode}
                </p>
              )}
            </div>
            <div className="mb-4">
              <label htmlFor="password" className="block text-gray-700 mb-1">
                New password*
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={handleChange}
                className={`w-full border ${
                  errors.password ? "border-red-500" : "border-gray-300"
                } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700`}
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
            <div className="mb-6">
              <label
                htmlFor="passwordAgain"
                className="block text-gray-700 mb-1"
              >
                New password again*
              </label>
              <input
                id="passwordAgain"
                name="passwordAgain"
                type="password"
                value={passwordAgain}
                onChange={handleChange}
                className={`w-full border ${
                  errors.passwordAgain ? "border-red-500" : "border-gray-300"
                } rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700`}
                autoComplete="new-password"
              />
              {errors.passwordAgain && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.passwordAgain}
                </p>
              )}
            </div>
            <div className="mb-4">
              <button
                type="submit"
                className={`w-full py-2 rounded-md text-white text-base font-medium transition-colors ${
                  loading
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-primary hover:bg-blue-950"
                }`}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                    Updating...
                  </span>
                ) : (
                  "Update password"
                )}
              </button>
            </div>
          </form>
          <div className="flex justify-end w-full text-sm mt-2">
            <a href="/login" className="text-lightblue-primary hover:underline">
              Sign In
            </a>
          </div>
        </div>
      </div>
      <footer className="w-full border-t border-gray-200 py-4 px-4 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500 bg-white">
        <div>Copyright © R-Link LLC 2025</div>
        <div>
          Support:{" "}
          <a
            href="mailto:support@r-link.com"
            className="text-lightblue-primary hover:underline"
          >
            support@r-link.com
          </a>
        </div>
      </footer>
    </div>
  );
};

export default RecoveryWithCode;
